<script lang="ts">
  import { tick } from 'svelte'

  import { fmtCoords } from '../../format'
  import { isIos, isStandalone } from '../../pwa.svelte'
  import {
    getPlace,
    MAX_NAME,
    mergePlaces,
    mergeText,
    movePlace,
    movePlaceTo,
    parsePlacesLink,
    places,
    placesLink,
    readPersisted,
    removePlace,
    renamePlace,
    type SavedPlace,
  } from '../../state/places.svelte'
  import { disableAlerts, enableAlerts, push, pushSupport } from '../../state/push.svelte'
  import Icon from '../Icon.svelte'
  import { haptic, isNativeShell, nativeShare } from '../../native'

  /**
   * The saved list (Phase 9 plan §6.5–6.7), as round 2 draws it: a grip, the
   * name and what it is, the alert bell, rename and remove. Then copy them to
   * another device as a link, or add a pasted one.
   *
   * The grip drags. Phase 9 chose buttons over a drag because a touch drag
   * inside the scrolling sheet fought the sheet's own; Settings is a section
   * now, so nothing under the finger competes. The grip is also a
   * button that takes the arrow keys, which is the keyboard's way to reorder.
   */

  let { headingId = 'settings-places' }: { headingId?: string } = $props()

  let note = $state('')
  let copied = $state('')
  let pasted = $state('')
  let pasteNote = $state('')
  let list: HTMLElement | undefined = $state()
  let flash = $state<string | null>(null)

  /** The place being renamed, and what the field says so far. */
  let editing = $state<string | null>(null)
  let draft = $state('')

  $effect(() => {
    void readPersisted()
  })

  // The filled star: bring that row into view and mark it. Not the rename
  // field's focus, which on a phone would put the keyboard over the list.
  $effect(() => {
    const id = places.focus
    if (!id || !list) return
    const row = list.querySelector<HTMLElement>(`[data-place="${CSS.escape(id)}"]`)
    places.focus = null
    if (!row) return
    flash = id
    setTimeout(() => {
      row.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
      row.focus({ preventScroll: true })
    }, 250)
    setTimeout(() => (flash = null), 2000)
  })

  /** "64106 · default", "was Lawrence, KS", or the coordinates. */
  function meta(place: SavedPlace): string {
    const parts = [
      place.zip ?? null,
      place.label && place.label !== place.name ? `was ${place.label}` : null,
    ].filter(Boolean)
    if (parts.length === 0) parts.push(fmtCoords(place.lat, place.lon))
    if (places.openTo === place.id) parts.push('default')
    return parts.join(' · ')
  }

  async function startRename(place: SavedPlace) {
    editing = place.id
    draft = place.name
    await tick()
    const field = list?.querySelector<HTMLInputElement>(`[data-rename="${CSS.escape(place.id)}"]`)
    field?.focus()
    field?.select()
  }

  async function finishRename(id: string, save: boolean) {
    if (save) renamePlace(id, draft)
    editing = null
    await tick()
    list?.querySelector<HTMLElement>(`[data-rename-button="${CSS.escape(id)}"]`)?.focus()
  }

  function remove(id: string) {
    const name = getPlace(id)?.name ?? 'That place'
    const { wasDefault } = removePlace(id)
    note = wasDefault
      ? `Removed ${name}. It was the default, so the app now opens to My location.`
      : `Removed ${name}.`
  }

  async function copyLink() {
    // In the app the page's origin is app://fusionwx, which no other device
    // can open: the app shares the path on the server's origin instead.
    // Never the clipboard fallback there: it would copy that origin.
    if (isNativeShell()) {
      const shared = await nativeShare(placesLink(places.list, ''), 'Fusion WX places')
      copied = ''
      note = shared ? '' : "Couldn't open the share sheet. Try again."
      return
    }
    const link = placesLink(places.list, window.location.origin)
    try {
      await navigator.clipboard.writeText(link)
      copied = ''
      note = 'Link copied. Open it on the other device, or paste it into Settings there.'
    } catch {
      // No clipboard (plain http, or refused): show it to copy by hand.
      copied = link
      note = ''
    }
  }

  function addPasted(e: SubmitEvent) {
    e.preventDefault()
    const incoming = parsePlacesLink(pasted)
    if (!incoming) {
      pasteNote = "That isn't a Fusion WX places link."
      return
    }
    pasteNote = mergeText(mergePlaces(incoming))
    pasted = ''
  }

  // A bell only where one can work: the server has push and this browser can
  // receive it. Settings → Notifications says why when it cannot.
  let bells = $derived(pushSupport() === 'ready')

  function bell(place: SavedPlace) {
    // Straight from the click, with no await before it: the permission
    // prompt inside `enableAlerts` is allowed only during the tap (§9.1).
    if (place.alerts) void disableAlerts(place.id)
    else void enableAlerts(place.id)
    haptic('impact')
  }

  // ---------- reordering ----------

  interface Drag {
    id: string
    from: number
    to: number
    dy: number
    startY: number
    rowH: number
  }

  let drag = $state<Drag | null>(null)

  function onGripDown(e: PointerEvent & { currentTarget: HTMLElement }, id: string, index: number) {
    if (e.button !== 0) return
    const row = e.currentTarget.closest('li')
    e.currentTarget.setPointerCapture?.(e.pointerId)
    drag = { id, from: index, to: index, dy: 0, startY: e.clientY, rowH: row?.offsetHeight || 48 }
  }

  function onGripMove(e: PointerEvent) {
    if (!drag) return
    const dy = e.clientY - drag.startY
    const to = Math.max(0, Math.min(places.list.length - 1, drag.from + Math.round(dy / drag.rowH)))
    drag = { ...drag, dy, to }
  }

  function onGripUp() {
    if (!drag) return
    const { id, from, to } = drag
    drag = null
    if (to !== from) movePlaceTo(id, to)
  }

  /** How far a row that is not being dragged moves out of the way. */
  function shift(index: number): number {
    if (!drag || index === drag.from) return 0
    if (drag.from < drag.to && index > drag.from && index <= drag.to) return -drag.rowH
    if (drag.from > drag.to && index >= drag.to && index < drag.from) return drag.rowH
    return 0
  }

  async function onGripKey(e: KeyboardEvent, id: string) {
    const delta = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
    if (!delta) return
    e.preventDefault()
    movePlace(id, delta)
    // The keyed row moves in the DOM, which can drop focus; put it back.
    await tick()
    list?.querySelector<HTMLElement>(`[data-grip="${CSS.escape(id)}"]`)?.focus()
  }

  // §6.6: what the browser has promised about keeping these.
  let iosTab = $derived(isIos() && !isStandalone())
  let storageLine = $derived(
    places.persisted
      ? 'Saved on this device.'
      : iosTab
        ? 'Safari may clear these after 7 days without a visit. Add the app to your Home Screen to keep them.'
        : 'Saved in this browser.',
  )
</script>

<div class="wx-rule">
  <h3 id={headingId} class="eyebrow">Places · {places.list.length}</h3>
  {#if bells && places.list.length > 0}<span class="hint">Bell: alerts for that place</span>{/if}
</div>

{#if places.list.length === 0}
  <p class="empty">No saved places yet. Tap the star beside a place's name to save it.</p>
{:else}
  <p id="places-grip-hint" class="visually-hidden">Drag, or use the up and down arrow keys, to reorder.</p>
  <ul class="rows" bind:this={list}>
    {#each places.list as place, i (place.id)}
      {@const moving = drag?.id === place.id}
      <li
        data-place={place.id}
        tabindex="-1"
        class:flash={flash === place.id}
        class:moving
        style:transform={moving
          ? `translateY(${drag?.dy ?? 0}px)`
          : shift(i)
            ? `translateY(${shift(i)}px)`
            : undefined}
      >
        {#if editing === place.id}
          <form
            class="rename"
            onsubmit={(e) => {
              e.preventDefault()
              void finishRename(place.id, true)
            }}
          >
            <input
              class="wx-field touch-target"
              type="text"
              data-rename={place.id}
              bind:value={draft}
              maxlength={MAX_NAME}
              aria-label={`Name of ${place.name}`}
              onkeydown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  void finishRename(place.id, false)
                }
              }}
            />
            <span class="rename-actions">
              <button type="submit" class="wx-btn ink touch-target">Save</button>
              <button
                type="button"
                class="wx-btn quiet touch-target"
                onclick={() => void finishRename(place.id, false)}>Cancel</button
              >
              {#if place.label && place.label !== place.name}
                <span class="sub">was {place.label}</span>
              {/if}
            </span>
          </form>
        {:else}
          <button
            type="button"
            class="grip"
            data-grip={place.id}
            aria-label={`Move ${place.name}`}
            aria-describedby="places-grip-hint"
            onpointerdown={(e) => onGripDown(e, place.id, i)}
            onpointermove={onGripMove}
            onpointerup={onGripUp}
            onpointercancel={onGripUp}
            onkeydown={(e) => void onGripKey(e, place.id)}
          >
            <Icon name="grip-vertical" size={18} strokeWidth={moving ? 2.5 : 2} />
          </button>
          <span class="text">
            <span class="name">{place.name}</span>
            <span class="sub">
              {moving && drag ? `Moving · ${drag.to + 1} of ${places.list.length}` : meta(place)}
            </span>
          </span>
          {#if bells}
            <button
              type="button"
              class="icon touch-target"
              class:off={!place.alerts}
              aria-label={`Alerts for ${place.name}`}
              aria-pressed={!!place.alerts}
              disabled={push.busy}
              onclick={() => bell(place)}
            >
              <Icon name={place.alerts ? 'bell' : 'bell-off'} size={18} filled={!!place.alerts} />
            </button>
          {:else}
            <span></span>
          {/if}
          <button
            type="button"
            class="icon touch-target"
            data-rename-button={place.id}
            aria-label={`Rename ${place.name}`}
            onclick={() => void startRename(place)}
          >
            <Icon name="pencil" size={17} />
          </button>
          <button
            type="button"
            class="icon touch-target"
            aria-label={`Remove ${place.name}`}
            onclick={() => remove(place.id)}
          >
            <Icon name="x" size={18} strokeWidth={2.5} />
          </button>
        {/if}
      </li>
    {/each}
  </ul>
{/if}

{#if note}<p class="note" role="status">{note}</p>{/if}

<div class="share">
  <button
    type="button"
    class="wx-btn touch-target"
    disabled={places.list.length === 0}
    onclick={copyLink}
  >
    <Icon name="share" size={17} strokeWidth={2.5} />{isNativeShell() ? 'Share link' : 'Copy share link'}
  </button>
  <span class="sub">The link carries names and coordinates only. No account.</span>
  {#if copied}
    <input
      class="wx-field link touch-target"
      type="text"
      readonly
      value={copied}
      aria-label="Link to these places"
      onfocus={(e) => e.currentTarget.select()}
    />
  {/if}
</div>

<form class="paste" onsubmit={addPasted} autocomplete="off">
  <label class="paste-label" for="places-paste">Add places from a link</label>
  <div class="paste-row">
    <input
      id="places-paste"
      class="wx-field touch-target"
      type="text"
      placeholder="Paste a Fusion WX link"
      bind:value={pasted}
    />
    <button type="submit" class="wx-btn ink touch-target" disabled={!pasted.trim()}>Add</button>
  </div>
  {#if pasteNote}<p class="note" role="status">{pasteNote}</p>{/if}
</form>

<p class="storage">{storageLine}</p>
{#if iosTab}
  <p class="storage">The installed app keeps its own list.</p>
{/if}

<style>
  h3 {
    margin: 0;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    position: relative;
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 32px 32px 32px;
    align-items: center;
    min-height: var(--place-row, 48px);
    border-bottom: 1px solid var(--color-divider);
    background: var(--color-bg);
    transition:
      transform 0.15s ease,
      background 0.3s ease;
  }

  li.moving {
    z-index: 2;
    border: 2px solid var(--color-text);
    margin: 0 -8px 0 -2px;
    box-shadow: var(--shadow-lg);
    transition: none;
  }

  li.flash {
    background: color-mix(in srgb, var(--color-warn) 16%, transparent);
  }

  li:focus {
    outline: none;
  }

  .grip {
    width: 28px;
    height: 100%;
    min-height: 40px;
    display: grid;
    place-items: center start;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-muted);
    cursor: grab;
    /* The finger drags the row, not the screen. */
    touch-action: none;
  }

  li.moving .grip {
    color: var(--color-text);
    cursor: grabbing;
  }

  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .name {
    font-size: var(--place-fs, 13px);
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sub {
    font-size: 11px;
    color: var(--color-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon {
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .icon.off {
    color: var(--color-muted);
  }

  .icon:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .rename {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 0 12px 28px;
  }

  .rename input {
    box-shadow: inset 0 -3px 0 var(--color-text);
  }

  .rename-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .rename-actions .wx-btn {
    --btn-h: 30px;
    font-size: 12px;
  }

  input {
    /* 16px stops iOS Safari zooming the viewport when the field is focused. */
    font-size: 16px;
  }

  @media (pointer: fine) {
    input {
      font-size: 13px;
    }
  }

  .share {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 12px;
  }

  .share .wx-btn {
    --btn-h: 34px;
  }

  .link {
    width: 100%;
  }

  .paste {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 14px;
  }

  .paste-label {
    font-size: 12px;
    font-weight: 800;
  }

  .paste-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .paste-row input {
    border-right: 0;
  }

  .paste-row .wx-btn {
    --btn-h: 36px;
    padding: 0 14px;
  }

  .empty,
  .note,
  .storage {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 1.45;
  }

  .empty,
  .storage {
    color: var(--color-muted);
  }
</style>
